import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../lib/axios';
import { createContact, updateContact } from '../../services/apiContacts';
import { createActivity } from '../../services/apiActivities';
import { FOLLOW_UP_TYPES, getDefaultFollowUpDate } from '../../lib/followUpTypes';

const EMPTY_FORM = {
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    phone: '',
    whatsapp_country: '+34',
    whatsapp_number: '',
    address: '',
    segmentation: '',
    tags: [],
    newTag: '',
    scheduleFollowUp: false,
    followUpType: 'call',
    followUpTitle: '',
    followUpDate: '',
    followUpNote: '',
};

export default function ContactModal({ isOpen, onClose, contact = null, defaultPipelineStage = null, onCreated = null }) {
    const queryClient = useQueryClient();

    // Initial State based on the Contact model
    const [formData, setFormData] = useState(EMPTY_FORM);

    // Campos personalizados del tenant. Se piden solo cuando el modal esta
    // abierto para no cargarlos en cada render de la lista de contactos.
    const { data: cfResp } = useQuery({
        queryKey: ['custom-fields', 'contact'],
        queryFn: () => axios.get('/api/custom-fields', { params: { entity_type: 'contact' } }),
        enabled: isOpen,
    });
    // withoutWrapping() esta activo, asi que la coleccion vuelve como array
    // plano; se contemplan las dos formas por si esa configuracion cambia.
    const customFields = Array.isArray(cfResp?.data) ? cfResp.data : (cfResp?.data?.data || []);

    // { [custom_field_id]: valor }
    const [customValues, setCustomValues] = useState({});

    // Precarga de los valores ya guardados. Va aparte del formData porque se
    // indexan por id de campo, no por nombre de columna.
    useEffect(() => {
        if (!contact) { setCustomValues({}); return; }
        const pares = {};
        (contact.custom_field_values || []).forEach((v) => {
            if (v.field_id != null) pares[v.field_id] = v.value ?? '';
        });
        setCustomValues(pares);
    }, [contact]);

    // Populate data if editing
    useEffect(() => {
        if (contact) {
            setFormData({
                ...EMPTY_FORM,
                first_name: contact.first_name || '',
                last_name: contact.last_name || '',
                company: contact.company || '',
                email: contact.email || '',
                phone: contact.phone || '',
                tags: contact.tags || [],
                whatsapp_country: '+34', // Can be parsed if you store it structured
                address: contact.address || '',
                segmentation: contact.segmentation || '',
            });
        } else {
            // Reset form for a new contact
            setFormData(EMPTY_FORM);
        }
    }, [contact, isOpen]);

    // Mutation for creating (optionally with a follow-up activity) or updating
    const mutation = useMutation({
        mutationFn: async (data) => {
            if (contact) {
                return updateContact({ id: contact.id, ...data });
            }

            const payload = { ...data };
            if (defaultPipelineStage) payload.pipeline_stage = defaultPipelineStage;
            payload.source = payload.source || 'manual';
            const { scheduleFollowUp, followUpType, followUpTitle, followUpDate, followUpNote, ...contactPayload } = payload;

            const created = await createContact(contactPayload);

            let followUpFailed = false;
            if (scheduleFollowUp) {
                try {
                    await createActivity({
                        contact_id: created.id,
                        type: followUpType,
                        title: followUpTitle.trim() || `Seguimiento con ${created.full_name || created.first_name || 'el lead'}`,
                        description: followUpNote || null,
                        scheduled_at: followUpDate || null,
                    });
                } catch (activityErr) {
                    console.error('Contact created but follow-up activity failed:', activityErr);
                    followUpFailed = true;
                }
            }

            return { ...created, __followUpFailed: followUpFailed };
        },
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['contacts'] });
            queryClient.invalidateQueries({ queryKey: ['contact'] });
            if (onCreated) onCreated(result);
            if (result?.__followUpFailed) {
                alert('El lead se creó correctamente, pero no se pudo agendar el seguimiento. Podés agendarlo luego desde su ficha.');
            }
            onClose();
        },
        onError: (err) => {
            console.error('Failed to save contact:', err);
            // We can add toast notifications here next
        }
    });

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFullNameChange = (e) => {
        // Simple logic to split a single "Full Name" input into first and last name
        // (matching user's provided HTML but mapping to backend requirements)
        const parts = e.target.value.split(' ');
        const first_name = parts[0] || '';
        const last_name = parts.slice(1).join(' ') || '';
        setFormData(prev => ({ ...prev, first_name, last_name, tempFullName: e.target.value }));
    };

    const getFullName = () => {
        if (formData.tempFullName !== undefined) return formData.tempFullName;
        return [formData.first_name, formData.last_name].filter(Boolean).join(' ');
    };

    const handleAddTag = (e) => {
        if (e.key === 'Enter' && formData.newTag.trim() !== '') {
            e.preventDefault();
            if (!formData.tags.includes(formData.newTag.trim())) {
                setFormData(prev => ({
                    ...prev,
                    tags: [...prev.tags, prev.newTag.trim()],
                    newTag: ''
                }));
            }
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const toggleFollowUp = () => {
        setFormData(prev => ({
            ...prev,
            scheduleFollowUp: !prev.scheduleFollowUp,
            followUpDate: !prev.scheduleFollowUp && !prev.followUpDate ? getDefaultFollowUpDate() : prev.followUpDate,
        }));
    };

    const handleSubmit = () => {
        // Construct final payload according to Backend needs
        const payload = {
            first_name: formData.first_name,
            last_name: formData.last_name,
            company: formData.company,
            email: formData.email,
            phone: formData.phone || '',
            address: formData.address,
            segmentation: formData.segmentation,
            tags: formData.tags,
            // syncCustomFields espera [{field_id, value}].
            custom_fields: Object.entries(customValues)
                .filter(([, value]) => value !== '' && value !== null && value !== undefined)
                .map(([fieldId, value]) => ({ field_id: Number(fieldId), value: String(value) })),
        };
        if (!contact) {
            payload.scheduleFollowUp = formData.scheduleFollowUp;
            payload.followUpType = formData.followUpType;
            payload.followUpTitle = formData.followUpTitle;
            payload.followUpDate = formData.followUpDate;
            payload.followUpNote = formData.followUpNote;
        }
        mutation.mutate(payload);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
            {/* Modal Container */}
            <div className="relative w-full max-w-2xl bg-white dark:bg-background-dark rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100 dark:border-primary/10">
                    <div className="flex items-center gap-3">
                        <div className="size-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                            <span className="material-symbols-outlined">{contact ? 'edit' : 'person_add'}</span>
                        </div>
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                            {contact ? 'Editar Contacto' : (defaultPipelineStage ? 'Nuevo Lead' : 'Nuevo Contacto')}
                        </h2>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                {/* Modal Body (Scrollable) */}
                <div className="flex-1 overflow-y-auto px-8 py-6 space-y-8">
                    {/* Section: Información Básica */}
                    <section>
                        <div className="flex items-center gap-2 mb-4">
                            <span className="material-symbols-outlined text-primary text-sm">info</span>
                            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Información Básica</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Nombre Completo</label>
                                <input
                                    className="w-full h-12 rounded-lg border-slate-200 focus:border-primary focus:ring-primary/20 bg-background-light/50 transition-all placeholder:text-slate-400 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                    placeholder="Ej: Juan Pérez"
                                    type="text"
                                    value={getFullName()}
                                    onChange={handleFullNameChange}
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Empresa</label>
                                <input
                                    className="w-full h-12 rounded-lg border-slate-200 focus:border-primary focus:ring-primary/20 bg-background-light/50 transition-all placeholder:text-slate-400 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                    placeholder="Nombre de la compañía"
                                    type="text"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Email</label>
                                <input
                                    className="w-full h-12 rounded-lg border-slate-200 focus:border-primary focus:ring-primary/20 bg-background-light/50 transition-all placeholder:text-slate-400 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                    placeholder="correo@ejemplo.com"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Teléfono</label>
                                <input
                                    className="w-full h-12 rounded-lg border-slate-200 focus:border-primary focus:ring-primary/20 bg-background-light/50 transition-all placeholder:text-slate-400 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                    placeholder="+34 600 000 000"
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Dirección / Ubicación</label>
                                <textarea
                                    className="w-full h-20 rounded-lg border-slate-200 focus:border-primary focus:ring-primary/20 bg-background-light/50 transition-all placeholder:text-slate-400 dark:border-gray-700 dark:bg-gray-800 dark:text-white p-3"
                                    placeholder="Ej: Calle Mayor 123, Madrid"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    rows={2}
                                />
                            </div>
                        </div>
                    </section>

                    {/* Section: Social / Omnicanal */}
                    <section>
                        <div className="flex items-center gap-2 mb-4">
                            <span className="material-symbols-outlined text-primary text-sm">chat_bubble</span>
                            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Conectividad Omnicanal</h3>
                        </div>
                        <div className="space-y-4">
                            <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Número de WhatsApp</label>
                            <div className="flex">
                                <div className="relative">
                                    <select
                                        name="whatsapp_country"
                                        value={formData.whatsapp_country}
                                        onChange={handleChange}
                                        className="h-12 pl-4 pr-10 rounded-l-lg border-r-0 border-slate-200 bg-slate-50 focus:ring-0 focus:border-slate-200 appearance-none font-medium text-slate-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300"
                                    >
                                        <option value="+34">🇪🇸 +34</option>
                                        <option value="+52">🇲🇽 +52</option>
                                        <option value="+57">🇨🇴 +57</option>
                                        <option value="+54">🇦🇷 +54</option>
                                        <option value="+1">🇺🇸 +1</option>
                                    </select>
                                </div>
                                <div className="relative flex-1">
                                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">chat</span>
                                    <input
                                        className="w-full h-12 pl-10 rounded-r-lg border-slate-200 focus:border-primary focus:ring-primary/20 bg-background-light/50 transition-all dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                        placeholder="600 000 000"
                                        type="text"
                                        name="whatsapp_number"
                                        value={formData.whatsapp_number}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Section: Etiquetas */}
                    <section>
                        <div className="flex items-center gap-2 mb-4">
                            <span className="material-symbols-outlined text-primary text-sm">sell</span>
                            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Categorización y Etiquetas</h3>
                        </div>
                        <div className="grid grid-cols-1 gap-5 mb-5">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Segmentación (Categoría)</label>
                                <input
                                    className="w-full h-12 rounded-lg border-slate-200 focus:border-primary focus:ring-primary/20 bg-background-light/50 transition-all placeholder:text-slate-400 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                    placeholder="Ej: Mayorista, Retail, VIP..."
                                    type="text"
                                    name="segmentation"
                                    value={formData.segmentation}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Etiquetas</label>
                        <div className="p-4 rounded-lg border border-dashed border-slate-300 bg-slate-50/50 dark:border-gray-700 dark:bg-gray-800/50">
                            <div className="flex flex-wrap gap-2 mb-3">
                                {formData.tags.map((tag, idx) => (
                                    <span key={idx} className="inline-flex items-center gap-1.5 px-3 py-1 bg-primary/10 text-primary text-sm font-bold rounded-full">
                                        {tag}
                                        <button onClick={() => handleRemoveTag(tag)} className="hover:text-primary/70">
                                            <span className="material-symbols-outlined text-[14px]">close</span>
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <input
                                className="w-full bg-transparent border-none focus:ring-0 text-sm p-0 placeholder:text-slate-400 dark:text-white"
                                placeholder="Escribe y presiona Enter para agregar etiquetas..."
                                type="text"
                                name="newTag"
                                value={formData.newTag}
                                onChange={handleChange}
                                onKeyDown={handleAddTag}
                            />
                        </div>
                    </section>

                    {/* Section: Agendar Seguimiento (solo al crear) */}
                    {!contact && (
                        <section className="border-t border-slate-100 pt-6 dark:border-gray-800">
                            <button type="button" onClick={toggleFollowUp} className="flex items-center justify-between w-full text-left group">
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-primary text-sm">event_available</span>
                                    <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Agendar Seguimiento</h3>
                                    <span className="text-[10px] font-semibold normal-case tracking-normal text-slate-400">(opcional)</span>
                                </div>
                                <span
                                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${formData.scheduleFollowUp ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700'}`}
                                >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.scheduleFollowUp ? 'translate-x-6' : 'translate-x-1'}`} />
                                </span>
                            </button>

                            {formData.scheduleFollowUp && (
                                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5 bg-primary/5 border border-primary/10 rounded-lg p-4">
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Tipo</label>
                                        <select
                                            name="followUpType"
                                            value={formData.followUpType}
                                            onChange={handleChange}
                                            className="w-full h-12 rounded-lg border-slate-200 focus:border-primary focus:ring-primary/20 bg-white dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                        >
                                            {FOLLOW_UP_TYPES.map(t => (
                                                <option key={t.value} value={t.value}>{t.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Fecha y hora</label>
                                        <input
                                            type="datetime-local"
                                            name="followUpDate"
                                            value={formData.followUpDate}
                                            onChange={handleChange}
                                            className="w-full h-12 rounded-lg border-slate-200 focus:border-primary focus:ring-primary/20 bg-white dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Título</label>
                                        <input
                                            type="text"
                                            name="followUpTitle"
                                            placeholder={`Ej: Llamar a ${getFullName() || 'este lead'} para confirmar interés`}
                                            value={formData.followUpTitle}
                                            onChange={handleChange}
                                            className="w-full h-12 rounded-lg border-slate-200 focus:border-primary focus:ring-primary/20 bg-white dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Nota (opcional)</label>
                                        <textarea
                                            name="followUpNote"
                                            value={formData.followUpNote}
                                            onChange={handleChange}
                                            rows={2}
                                            className="w-full rounded-lg border-slate-200 focus:border-primary focus:ring-primary/20 bg-white dark:border-gray-700 dark:bg-gray-800 dark:text-white p-3"
                                        />
                                    </div>
                                </div>
                            )}
                        </section>
                    )}

                    {/* Section: Campos Personalizados */}
                    {customFields.length > 0 && (
                    <section className="border-t border-slate-100 pt-6 dark:border-gray-800">
                        <div className="flex items-center gap-2 mb-4">
                            <span className="material-symbols-outlined text-primary text-sm">tune</span>
                            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">Campos Personalizados</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {customFields.map((f) => (
                                <div key={f.id} className="col-span-2 md:col-span-1">
                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                                        {f.name}
                                    </label>
                                    {f.type === 'select' ? (
                                        <select
                                            value={customValues[f.id] ?? ''}
                                            onChange={(e) => setCustomValues((v) => ({ ...v, [f.id]: e.target.value }))}
                                            className="w-full rounded-lg border-slate-200 focus:border-primary focus:ring-primary/20 bg-white dark:border-gray-700 dark:bg-gray-800 dark:text-white p-3"
                                        >
                                            <option value="">Sin definir</option>
                                            {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                    ) : f.type === 'checkbox' ? (
                                        <label className="flex items-center gap-2 p-3">
                                            <input
                                                type="checkbox"
                                                checked={customValues[f.id] === '1'}
                                                onChange={(e) => setCustomValues((v) => ({ ...v, [f.id]: e.target.checked ? '1' : '0' }))}
                                                className="w-4 h-4 rounded"
                                            />
                                            <span className="text-sm dark:text-white">Si</span>
                                        </label>
                                    ) : (
                                        <input
                                            type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                                            value={customValues[f.id] ?? ''}
                                            onChange={(e) => setCustomValues((v) => ({ ...v, [f.id]: e.target.value }))}
                                            className="w-full rounded-lg border-slate-200 focus:border-primary focus:ring-primary/20 bg-white dark:border-gray-700 dark:bg-gray-800 dark:text-white p-3"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                    )}
                </div>

                {/* Footer Buttons */}
                <div className="px-8 py-6 bg-slate-50 dark:bg-slate-900/50 flex items-center justify-end gap-4 border-t border-slate-100 dark:border-primary/10">
                    <button onClick={onClose} className="px-6 py-2.5 text-slate-500 font-bold hover:text-slate-700 dark:hover:text-slate-300 transition-colors rounded-lg">
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={mutation.isPending || !formData.first_name.trim()}
                        title={!formData.first_name.trim() ? 'Ingresá al menos un nombre' : undefined}
                        className="px-8 py-2.5 bg-primary hover:bg-primary/90 text-white font-bold rounded-lg shadow-lg shadow-primary/20 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {mutation.isPending ? (
                            <span className="material-symbols-outlined text-[20px] animate-spin">refresh</span>
                        ) : (
                            <span className="material-symbols-outlined text-[20px]">save</span>
                        )}
                        {contact ? 'Actualizar Contacto' : (defaultPipelineStage ? 'Guardar Lead' : 'Guardar Contacto')}
                    </button>
                </div>

            </div>
        </div>
    );
}
